import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "report-preferences-in-depth",
  courseId: "your-style",
  order: 1,
  title: "Preferences in depth",
  summary:
    "Each field on the Report Preferences page changes something specific in the draft. This lesson maps fields to effects and explains what your department can push.",
  learn: [
    "Map each preference to its effect on the narrative",
    "Use conditional fields like rank and badge correctly",
    "Understand pushed department preferences",
    "Add names and terms to Common Spellings",
  ],
  minutes: 7,
  releasedAt: "2025-06-01T00:00:00.000Z",
  sceneId: "report-preferences-in-depth",
  tourId: "report-preferences-in-depth",
  body: [
    {
      type: "paragraph",
      text: "Report Preferences are sent to the AI with every generation. The page at /preferences (the sliders icon beside **New Case**) is the same on web and iOS, and a change on one shows on the other.",
    },
    { type: "heading", text: "Identity" },
    {
      type: "bullets",
      items: [
        "**Include Name** and the name itself: how the report refers to you in third person.",
        "**Include Rank/Title** with **Abbreviate Rank** and **Capitalize Rank**: \"Officer\", \"Ofc.\", or \"OFFICER\".",
        "**Include Badge Number**: appended after your name where your department's format calls for it.",
        "**Capitalize Names**: writes subject and witness surnames in capitals (REYES).",
        "Call Sign, with Format and Placement: see the Call signs lesson.",
      ],
    },
    { type: "heading", text: "Voice and format" },
    {
      type: "bullets",
      items: [
        "**Narrative Perspective**: first person (\"I contacted\") or third person (\"Officer Smith contacted\").",
        "**Subject Reference Style**: how people are referred to after first mention (role, surname, full name).",
        "**Date Format** and **Time Format**: 24-hour \"2214 hours\" versus \"10:14 PM\", and the date style.",
        "**Include Topic Headings**: section headings in the draft. **Include Statutes** takes your state, so statutes can appear in the draft.",
        "**Common Spellings**: names and terms the transcript keeps getting wrong. See the section below.",
      ],
    },
    { type: "heading", text: "Common Spellings" },
    {
      type: "paragraph",
      text: "Speech recognition does not know your beat. \"Alvarado\" becomes \"Alvarez\", and \"Sgt. Nguyen\" becomes something else entirely. **Common Spellings** is the list the AI consults when it writes, and the list the proofreader treats as correct.",
    },
    {
      type: "steps",
      items: [
        "Open **Report Preferences** and find **Common Spellings**.",
        "Type each term as you want it to appear in reports, then press Enter (a comma, space, or Tab also works). Each entry is one word: proper names, street names, business names, local slang the department uses.",
        "Click **Save Preferences**.",
      ],
    },
    {
      type: "paragraph",
      text: "You can also add a term while you edit. When the proofreader underlines a correct name in the editor, click it and choose **Add to dictionary**. That appends it to the same list.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "Add the names of your regular partners and supervisors. They show up in nearly every report and are among the most common transcript errors.",
    },
    {
      type: "callout",
      tone: "note",
      text: "Common Spellings guides the AI when it drafts. It does not rewrite the transcript itself. If the transcript shows the wrong name, correct it in the **Transcription** tab and regenerate.",
    },
    { type: "heading", text: "Check your changes" },
    {
      type: "steps",
      items: [
        "Change the fields you want, then click **Save Preferences**.",
        "Open a recent case and choose **Regenerate Narrative** from the **Narrative** dropdown.",
      ],
    },
    {
      type: "paragraph",
      text: "Regenerating on a real transcript is the quickest way to see what a setting does.",
    },
    {
      type: "callout",
      tone: "note",
      title: "Department push",
      text: "An agency admin can push a set of preferences to every officer from the dashboard's Report Preferences tab. Pushed values overwrite yours at the time of the push. Your call sign is never pushed. It is yours.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "Header, footer, and writing instructions also exist as preferences, but a report template overrides all three. If a template is selected on a case, edit the template, not the preference.",
    },
  ],
  related: ["set-up-your-report-preferences", "report-templates", "call-signs", "polish-the-draft"],
};

export default lesson;
